import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Chip, List, ListItemButton, ListItemText, Paper, TextField, Typography} from '@mui/material';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {Button} from 'reactstrap';
import {useNavigate} from 'react-router-dom';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import MessageTemplateApiService from '../../api/MessageTemplateApiService';
import {estimateSegments} from '../../utilities/SmsSegments';
import PageShell from '../PageShell';

/** Mirrors MessageTemplateChannel on the server. */
const Channel = {Sms: 1, Email: 2};

export default function MessageTemplates(props) {
    const {isMobile, setSnackbarProperties} = props;

    const messageTemplateApiService = new MessageTemplateApiService();
    const navigate = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [saving, setSaving] = useState(false);

    const selected = templates.find((template) => templateId(template) === selectedKey);

    const isDirty = selected && (selected.body !== body || selected.subject !== subject);

    // An SMS is billed per segment, so the owner needs to see the cost of their edit as they make it.
    const segments = useMemo(
        () => (selected?.channel === Channel.Sms ? estimateSegments(body) : 0),
        [selected, body]
    );

    useEffect(() => {
        if (!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);
            return;
        }

        if (!authenticationManager.isSuperAdmin()) {
            navigate('/');
            return;
        }

        document.title = 'Executive Storage Suites - Message Templates';

        void getTemplates();
    }, []);

    function templateId(template) {
        return `${template.channel}:${template.key}`;
    }

    async function getTemplates() {
        const response = await messageTemplateApiService.getEditableTemplates();

        if (response?.success) {
            setTemplates(response.data);

            if (response.data.length > 0 && selectedKey === null) {
                selectTemplate(response.data[0]);
            }
        } else {
            setSnackbarProperties(`Error loading templates: ${response?.message}`, 'error');
        }
    }

    function selectTemplate(template) {
        setSelectedKey(templateId(template));
        setSubject(template.subject ?? '');
        setBody(template.body ?? '');
    }

    function revert() {
        if (selected) {
            setSubject(selected.subject ?? '');
            setBody(selected.body ?? '');
        }
    }

    async function save() {
        setSaving(true);

        const response = await messageTemplateApiService.updateTemplate({
            channel: selected.channel,
            key: selected.key,
            subject,
            body,
        });

        if (response?.success) {
            // Replaced with exactly what the server stored, which may be trimmed compared to what was
            // typed, so the editor does not immediately look unsaved again.
            setTemplates(templates.map((template) =>
                templateId(template) === selectedKey ? response.data : template));
            setSubject(response.data.subject ?? '');
            setBody(response.data.body ?? '');
            setSnackbarProperties('Template saved', 'success');
        } else {
            setSnackbarProperties(response?.message ?? 'Unable to save the template', 'error');
        }

        setSaving(false);
    }

    function insertToken(token) {
        setBody(`${body}${token}`);
    }

    return (
        <PageShell
            title={'Message Templates'}
            subtitle={`${templates.length} editable message${templates.length === 1 ? '' : 's'}`}
            isMobile={isMobile}
            disableCard
        >
            <Typography sx={{marginBottom: '15px'}}>
                Wording for the automated messages. Changes take effect on the next message sent, with no deploy
                needed. Tokens in double braces are replaced with real values when the message goes out.
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper variant={'outlined'}>
                        <List dense>
                            {templates.map((template) => (
                                <ListItemButton
                                    key={templateId(template)}
                                    selected={templateId(template) === selectedKey}
                                    onClick={() => selectTemplate(template)}
                                >
                                    <ListItemText
                                        primary={template.displayName}
                                        secondary={template.updatedUtc
                                            ? `Edited ${new Date(template.updatedUtc).toLocaleDateString()}`
                                            : 'Original wording'}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    {selected &&
                        <Box>
                            <Typography variant={'body2'} sx={{marginBottom: '10px'}}>
                                {selected.description}
                            </Typography>

                            <Box sx={{marginBottom: '10px'}}>
                                <Typography variant={'body2'} sx={{marginBottom: '5px'}}>
                                    Available tokens — click to add one to the end of the message:
                                </Typography>
                                {selected.availableTokens.map((token) => (
                                    <Chip
                                        key={token}
                                        label={token}
                                        size={'small'}
                                        onClick={() => insertToken(token)}
                                        sx={{marginRight: '5px', marginBottom: '5px'}}
                                        color={selected.requiredTokens.includes(token) ? 'primary' : 'default'}
                                    />
                                ))}
                            </Box>

                            {selected.channel === Channel.Email &&
                                <TextField
                                    fullWidth
                                    label={'Subject'}
                                    value={subject}
                                    onChange={(event) => setSubject(event.target.value)}
                                    sx={{marginBottom: '10px'}}
                                />
                            }

                            <TextField
                                fullWidth
                                multiline
                                minRows={selected.channel === Channel.Sms ? 5 : 18}
                                label={'Message body'}
                                value={body}
                                onChange={(event) => setBody(event.target.value)}
                                sx={{marginBottom: '10px'}}
                            />

                            {selected.channel === Channel.Sms &&
                                <>
                                    <Typography variant={'body2'} sx={{marginBottom: '10px'}}>
                                        {`Roughly ${segments} segment${segments === 1 ? '' : 's'} before tokens are replaced. The real length depends on your business name and the values substituted in.`}
                                    </Typography>
                                    {segments > 1 &&
                                        <Alert severity={'warning'} sx={{marginBottom: '10px'}}>
                                            Messages over one segment are billed per segment, for every recipient.
                                        </Alert>
                                    }
                                    {!body.toUpperCase().includes('STOP') &&
                                        <Alert severity={'error'} sx={{marginBottom: '10px'}}>
                                            Every text must tell the recipient how to opt out. Carriers can suspend
                                            your number over a missing STOP notice, so this will not save.
                                        </Alert>
                                    }
                                </>
                            }

                            <Button
                                className={isMobile ? 'mobile-modal-button' : ''}
                                variant={'contained'}
                                disabled={!isDirty || saving}
                                onClick={save}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                className={isMobile ? 'mobile-modal-button' : ''}
                                disabled={!isDirty || saving}
                                onClick={revert}
                                style={{marginLeft: '10px'}}
                            >
                                Revert
                            </Button>
                        </Box>
                    }
                </Grid>
            </Grid>
        </PageShell>
    );
}

MessageTemplates.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};
